import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CalendarDays, Clock, MapPin, GraduationCap, Mail, Phone, ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import ScrollReveal from "@/components/ui/scroll-reveal";
const POSTER = "/images/corso-soccorritore-2026.jpg";

const EVENT_DATE = new Date("2026-10-20T21:00:00+02:00");

interface Props {
  variant?: "full" | "compact";
}

const CorsoSoccorritoreBanner = ({ variant = "full" }: Props) => {
  const { t } = useLanguage();
  const c = t.corsoSoccorritore;

  if (new Date() > EVENT_DATE) return null;

  const daysLeft = Math.max(0, Math.ceil((EVENT_DATE.getTime() - Date.now()) / (1000 * 60 * 60 * 24)));

  if (variant === "compact") {
    return (
      <ScrollReveal>
        <section className="py-8 px-4 sm:px-6 bg-primary/5 border-y border-primary/15">
          <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-5 lg:gap-8">
            <img
              src={POSTER}
              alt={c.posterAlt}
              className="w-24 sm:w-28 rounded-lg shadow-md shrink-0"
              loading="lazy"
            />
            <div className="flex-1 text-center lg:text-left">
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 mb-1.5">
                <span className="bg-accent text-accent-foreground text-[10px] font-bold px-2.5 py-0.5 rounded-full uppercase tracking-wide">
                  {c.badge}
                </span>
                <span className="text-xs font-semibold text-accent">
                  {daysLeft} {c.daysLeft}
                </span>
              </div>
              <h3 className="text-lg font-bold font-heading">
                {c.kicker} <span className="text-primary uppercase">{c.title}</span>
              </h3>
              <p className="text-sm text-muted-foreground mt-1 leading-relaxed">{c.intro}</p>
              <div className="mt-2 flex flex-col sm:flex-row justify-center lg:justify-start gap-x-5 gap-y-1 text-xs text-muted-foreground">
                <span className="flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5 text-primary" />{c.date}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5 text-primary" />{c.time}</span>
                <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-primary" />{c.venue}</span>
              </div>
            </div>
            <Button variant="cta" className="shrink-0" asChild>
              <Link to="/servizi/corsi-formazione-sanitaria#corso-soccorritore">
                {c.ctaMore} <ArrowRight className="w-4 h-4 ml-1" />
              </Link>
            </Button>
          </div>
        </section>
      </ScrollReveal>
    );
  }

  return (
    <ScrollReveal>
      <div id="corso-soccorritore" className="avps-card border-primary/25 bg-primary/5 scroll-mt-28">
        <div className="grid md:grid-cols-[280px_1fr] gap-6 md:gap-8 items-start">
          <img
            src={POSTER}
            alt={c.posterAlt}
            className="rounded-xl w-full object-cover shadow-md"
            loading="lazy"
          />
          <div>
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className="bg-accent text-accent-foreground text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide">
                {c.badge}
              </span>
              <span className="text-sm font-semibold text-accent">{daysLeft} {c.daysLeft}</span>
            </div>
            <p className="text-sm text-muted-foreground italic mb-1">{c.claim}</p>
            <h3 className="text-2xl font-bold font-heading mb-1">
              {c.kicker} <span className="text-primary uppercase">{c.title}</span>
            </h3>
            <p className="text-sm text-muted-foreground mb-4">{c.presentationNote}</p>

            <div className="grid sm:grid-cols-2 gap-3 mb-5 text-sm">
              <div className="flex items-start gap-2">
                <CalendarDays className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span className="font-semibold">{c.date} — {c.time}</span>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 text-primary mt-0.5 shrink-0" />
                <span><span className="font-semibold">{c.venue}</span><br /><span className="text-muted-foreground">{c.address}</span></span>
              </div>
            </div>

            <p className="text-sm leading-relaxed mb-5">{c.intro}</p>

            <h4 className="text-base font-semibold font-heading mb-3">{c.modulesTitle}</h4>
            <div className="grid sm:grid-cols-2 gap-3 mb-6">
              {c.modules.map((m) => (
                <div key={m.name} className="rounded-lg border border-border bg-card p-4">
                  <div className="flex items-center gap-2 mb-1">
                    <GraduationCap className="w-4 h-4 text-accent" />
                    <span className="font-bold text-sm">{m.name}</span>
                    <span className="text-xs text-muted-foreground">· {m.hours}</span>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{m.cert}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <Button variant="cta" asChild>
                <a href={`mailto:${c.email}?subject=Corso%20Regionale%20di%20Soccorritore`}>
                  <Mail className="w-4 h-4 mr-1" /> {c.ctaInfo}
                </a>
              </Button>
              <Button variant="outline" asChild>
                <a href="tel:0396612112">
                  <Phone className="w-4 h-4 mr-1" /> {c.ctaCall}
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </ScrollReveal>
  );
};

export default CorsoSoccorritoreBanner;
